// Image generation client — loads images through the global throttle
// Uses fetch → blob URL to serialize all Pollinations requests

import { getApiKey } from './api-key'
import { throttledFetch } from './throttle'

export interface ImageClientConfig {
  baseUrl: string
  defaultModel: string
  defaultWidth: number
  defaultHeight: number
}

export interface ImageGenerationOptions {
  model?: string
  width?: number
  height?: number
  seed?: number
}

// Call Pollinations directly — no proxy needed
const DEFAULT_BASE_URL = 'https://gen.pollinations.ai/image'
const DEFAULT_MODEL = 'z-image-turbo'
const DEFAULT_WIDTH = 768
const DEFAULT_HEIGHT = 576

/** Placeholder returned when image preloading fails. */
const PLACEHOLDER_IMAGE_URL =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="768" height="576" viewBox="0 0 768 576">' +
    '<rect width="768" height="576" fill="#f1f5f9"/>' +
    '<text x="384" y="288" text-anchor="middle" dominant-baseline="central" ' +
    'font-family="Inter, sans-serif" font-size="20" fill="#94a3b8">Image unavailable</text>' +
    '</svg>'
  )

export class ImageClient {
  private config: ImageClientConfig

  constructor(config?: Partial<ImageClientConfig>) {
    this.config = {
      baseUrl: config?.baseUrl ?? DEFAULT_BASE_URL,
      defaultModel: config?.defaultModel ?? DEFAULT_MODEL,
      defaultWidth: config?.defaultWidth ?? DEFAULT_WIDTH,
      defaultHeight: config?.defaultHeight ?? DEFAULT_HEIGHT,
    }
  }

  /**
   * Build the raw image URL for a given prompt (with API key).
   * Used internally — callers should prefer loadImageAsBlob() for throttled loading.
   */
  getImageUrl(prompt: string, options?: ImageGenerationOptions): string {
    const model = options?.model ?? this.config.defaultModel
    const width = options?.width ?? this.config.defaultWidth
    const height = options?.height ?? this.config.defaultHeight
    const seed = options?.seed ?? Math.floor(Math.random() * 2_147_483_647)

    const encodedPrompt = encodeURIComponent(prompt)

    const params = new URLSearchParams({
      model,
      width: String(width),
      height: String(height),
      seed: String(seed),
      nologo: 'true',
      key: getApiKey(),
    })

    return `${this.config.baseUrl}/${encodedPrompt}?${params.toString()}`
  }

  /**
   * Load an image through the global throttle and return a blob URL.
   * This ensures image requests are serialized with LLM calls.
   * Returns PLACEHOLDER_IMAGE_URL on failure.
   */
  async loadImageAsBlob(
    prompt: string,
    options?: Pick<ImageGenerationOptions, 'model' | 'width' | 'height'>
  ): Promise<string> {
    const url = this.getImageUrl(prompt, options)
    try {
      const response = await throttledFetch(url)
      if (!response.ok) {
        console.warn(`ImageClient: HTTP ${response.status} for image`)
        return PLACEHOLDER_IMAGE_URL
      }
      const blob = await response.blob()
      return URL.createObjectURL(blob)
    } catch (err) {
      console.warn(
        `ImageClient: failed to load image: ${err instanceof Error ? err.message : String(err)}`
      )
      return PLACEHOLDER_IMAGE_URL
    }
  }

  /**
   * Preload an image by creating an Image element and waiting for it to fully load.
   *
   * Returns the image URL on success, or a placeholder data URI on error.
   */
  async preloadImage(
    prompt: string,
    options?: Pick<ImageGenerationOptions, 'model' | 'width' | 'height'>
  ): Promise<string> {
    // Use throttled blob loading
    return this.loadImageAsBlob(prompt, options)
  }
}
